<template>
  <div
    v-if="features.length > 0"
    class="lightning-model-page__highlighted-features py-10"
    :style="backgroundStyle"
  >
    <div
      class="lightning-model-page__highlighted-features__header mb-5"
      :style="headerStyle"
      @mouseover="isHeaderHovered = true"
      @mouseleave="isHeaderHovered = false"
    >
      {{ featuresSection.header.text }}
    </div>
    <v-container>
      <v-row align="center" justify="space-around">
        <template v-for="(feature, index) in features" :key="index">
          <v-col class="text-center my-1 my-sm-5" sm="4" cols="12">
            <p>{{ feature.key }}</p>
            <p class="font-weight-bold" v-html="feature.value"></p>
          </v-col>

          <!-- Divider - mobile view -->
          <v-divider
            v-if="index < featuresSection.features.length - 1"
            thickness="2"
            color="white"
            class="d-flex d-sm-none text-center mx-10"
          ></v-divider>

          <!-- Divider - desktop view: Only render if it's not the last column -->
          <v-divider
            v-if="
              index < featuresSection.features.length - 1 && index % 3 !== 2
            "
            inset
            vertical
            thickness="2"
            color="white"
            class="lightning-model-page__divider--vertical d-none d-sm-flex"
          ></v-divider>
        </template>
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import DOMPurify from 'dompurify'
import type { CSSProperties } from 'vue'
import type { IColorSettings } from '@jazel/jazel-vuetify'
import type { IFeaturesSectionSettings } from '@/types'

const props = defineProps<{
  featuresSection: IFeaturesSectionSettings
  availableHighlightedFeatures: Array<{ key: string; value: string | string[] }>
}>()

const features = computed(() => {
  const featureSettings = props.featuresSection
  const featuresData = props.availableHighlightedFeatures

  // Map feature title with value
  const featureTitles = featureSettings.features
  const featureKeyValuePairs = featureTitles.map(title => {
    const featureData = featuresData.find(f => f.key === title)
    let featureValue = 'TBD'

    if (featureData && Array.isArray(featureData.value)) {
      featureValue = featureData.value.join('<br>')
    } else if (featureData && featureData.value !== '') {
      featureValue = String(featureData.value)
    }

    const sanitizedHtml = DOMPurify.sanitize(featureValue)

    return {
      key: title,
      value: sanitizedHtml,
    }
  })

  return featureKeyValuePairs ?? []
})

const isHovered = ref(false)
const isHeaderHovered = ref(false)
const backgroundStyle = computed(() => {
  const backgroundColors =
    props.featuresSection.generalSettings.backgroundColors ?? {}

  return {
    backgroundColor: isHovered.value
      ? (backgroundColors?.backgroundHover ?? 'inherit')
      : (backgroundColors?.background ?? 'inherit'),
    // '--hover-background-color': backgroundColors?.backgroundHover ?? 'inherit',
  }
})

const headerStyle = computed<CSSProperties>((): CSSProperties => {
  const typography = props.featuresSection.header.typography ?? {}
  const colors: IColorSettings = props.featuresSection.header.colors ?? {}
  const fontFamily = typography.fontFamily ?? 'inherit'
  const fontStyle = typography.fontStyle ?? 'normal'
  const fontWeight = typography.fontWeight ?? 'normal'
  const textDecoration = typography.textDecoration ?? 'none'
  const textAlign = typography.textAlignment ?? 'left'
  const textTransform = typography.textTransform ?? 'none'

  let fontSize = 'inherit'
  if (typography?.fontSize != null && typography?.fontSizeUnit != null) {
    fontSize = `${typography?.fontSize ?? ''}${typography?.fontSizeUnit ?? ''}`
  }

  let letterSpacing = 'inherit'
  if (
    typography?.letterSpacing != null &&
    typography?.letterSpacingUnit != null
  ) {
    letterSpacing = `${typography?.letterSpacing ?? ''}${typography?.letterSpacingUnit ?? ''}`
  }
  let lineHeight = 'inherit'
  if (typography?.lineHeight != null && typography?.lineHeightUnit != null) {
    lineHeight = `${typography?.lineHeight ?? ''}${typography?.lineHeightUnit ?? ''}`
  }

  return {
    color: isHeaderHovered.value
      ? (colors?.colorHover ?? 'inherit')
      : (colors?.color ?? 'inherit'),
    // '--hover-color': colors?.colorHover ?? 'inherit',
    fontFamily,
    fontSize,
    fontStyle,
    fontWeight,
    letterSpacing,
    lineHeight,
    textAlign,
    textDecoration,
    textTransform,
  } as CSSProperties
})
</script>

<style scoped>
.lightning-model-page__divider--vertical {
  margin-left: -2px;
}

@media all and (max-width: 767px) {
  .lightning-model-page__highlighted-features__header {
    font-size: 24px !important;
  }
}
</style>
